import {
  INVOICE_PAYMENT_STATUSES,
  INVOICE_STATUSES,
  type InvoicePaymentStatus,
  type InvoiceStatus,
} from '@monorepo/shared/schemas/common'
import { FilterIcon, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

export interface InvoiceFiltersType {
  field?: 'customer_name' | 'invoice_number' | 'organization_name'
  operator?: 'is' | 'starts_with' | 'ends_with' | 'contains'
  value?: string
  invoice_status?: InvoiceStatus[]
  payment_status?: InvoicePaymentStatus[]
}

interface FilterOption {
  value: string
  label: string
}

interface InvoiceFiltersProps {
  filters: InvoiceFiltersType
  onFiltersChange: (filters: InvoiceFiltersType) => void
  onSearch: () => void
  onReset: () => void
}

const formatLabel = (value: string) =>
  value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const filterFields: FilterOption[] = [
  { value: 'customer_name', label: 'Customer Name' },
  { value: 'invoice_number', label: 'Invoice Number' },
  { value: 'organization_name', label: 'Organization' },
]

const filterOperators: FilterOption[] = [
  { value: 'is', label: 'Is' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
  { value: 'contains', label: 'Contains' },
]

export function InvoiceFilters({
  filters,
  onFiltersChange,
  onSearch: _onSearch,
  onReset,
}: InvoiceFiltersProps) {
  const [selectedInvoiceStatuses, setSelectedInvoiceStatuses] = useState<
    InvoiceStatus[]
  >((filters.invoice_status as InvoiceStatus[]) || [])
  const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<
    InvoicePaymentStatus[]
  >((filters.payment_status as InvoicePaymentStatus[]) || [])

  // Local state for text input - debounced before sending to parent
  const [localValue, setLocalValue] = useState(filters.value || '')

  // Sync local value if filters.value is reset externally (e.g. on reset)
  useEffect(() => {
    setLocalValue(filters.value || '')
  }, [filters.value])

  // Debounce: only call onFiltersChange 500ms after user stops typing
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional - only debounce on localValue changes; filters/onFiltersChange are read inside timer to avoid resetting timer on unrelated filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== (filters.value || '')) {
        onFiltersChange({
          ...filters,
          value: localValue || undefined,
        })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [localValue])

  const updateFilter = (
    key: keyof InvoiceFiltersType,
    value: string | string[] | undefined
  ) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    })
  }

  const handleInvoiceStatusToggle = (
    statusValue: InvoiceStatus,
    checked: boolean
  ) => {
    const newStatuses = checked
      ? [...selectedInvoiceStatuses, statusValue]
      : selectedInvoiceStatuses.filter(s => s !== statusValue)

    setSelectedInvoiceStatuses(newStatuses)
    updateFilter('invoice_status', newStatuses)
  }

  const handlePaymentStatusToggle = (
    statusValue: InvoicePaymentStatus,
    checked: boolean
  ) => {
    const newStatuses = checked
      ? [...selectedPaymentStatuses, statusValue]
      : selectedPaymentStatuses.filter(s => s !== statusValue)

    setSelectedPaymentStatuses(newStatuses)
    updateFilter('payment_status', newStatuses)
  }

  const clearFilter = (key: keyof InvoiceFiltersType) => {
    if (key === 'invoice_status') {
      setSelectedInvoiceStatuses([])
    } else if (key === 'payment_status') {
      setSelectedPaymentStatuses([])
    }
    updateFilter(key, undefined)
  }

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (
      key === 'page' ||
      key === 'limit' ||
      key === 'sort' ||
      key === 'order'
    ) {
      return false
    }
    return (
      value !== undefined &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
    )
  })

  return (
    <div className='mb-6'>
      <div className='flex gap-3 items-end justify-between'>
        <div className='flex gap-3 items-center'>
          <div className='w-40'>
            <Select
              value={filters.field || ''}
              onValueChange={value =>
                updateFilter('field', value as InvoiceFiltersType['field'])
              }
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue placeholder='Select field' />
              </SelectTrigger>
              <SelectContent>
                {filterFields.map(field => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='w-28'>
            <Select
              value={filters.operator || ''}
              onValueChange={value =>
                updateFilter(
                  'operator',
                  value as InvoiceFiltersType['operator']
                )
              }
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue placeholder='Operator' />
              </SelectTrigger>
              <SelectContent>
                {filterOperators.map(operator => (
                  <SelectItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='w-80'>
            <Input
              placeholder='Search value'
              value={localValue}
              onChange={e => setLocalValue(e.target.value)}
              className='h-8 text-xs'
            />
          </div>
        </div>

        <div className='flex gap-3 items-center'>
          <div className='w-48'>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-full h-8 text-xs justify-start'
                >
                  <FilterIcon className='w-3 h-3 mr-1' />
                  Invoice Status ({selectedInvoiceStatuses.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-48' align='start'>
                <div className='space-y-2'>
                  <div className='font-medium text-sm'>
                    Select Invoice Status
                  </div>
                  {INVOICE_STATUSES.map(status => (
                    <div key={status} className='flex items-center space-x-2'>
                      <Checkbox
                        id={`invoice-status-${status}`}
                        checked={selectedInvoiceStatuses.includes(status)}
                        onCheckedChange={checked =>
                          handleInvoiceStatusToggle(status, checked as boolean)
                        }
                      />
                      <Label
                        htmlFor={`invoice-status-${status}`}
                        className='text-sm'
                      >
                        {formatLabel(status)}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className='w-48'>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-full h-8 text-xs justify-start'
                >
                  <FilterIcon className='w-3 h-3 mr-1' />
                  Payment Status ({selectedPaymentStatuses.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-48' align='start'>
                <div className='space-y-2'>
                  <div className='font-medium text-sm'>
                    Select Payment Status
                  </div>
                  {INVOICE_PAYMENT_STATUSES.map(status => (
                    <div key={status} className='flex items-center space-x-2'>
                      <Checkbox
                        id={`payment-status-${status}`}
                        checked={selectedPaymentStatuses.includes(status)}
                        onCheckedChange={checked =>
                          handlePaymentStatusToggle(status, checked as boolean)
                        }
                      />
                      <Label
                        htmlFor={`payment-status-${status}`}
                        className='text-sm'
                      >
                        {formatLabel(status)}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {hasActiveFilters && (
            <Button
              variant='outline'
              size='sm'
              onClick={onReset}
              className='h-8'
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {hasActiveFilters && (
        <div className='flex gap-2 flex-wrap mt-3'>
          <span className='text-sm text-muted-foreground'>Active filters:</span>

          {filters.field && filters.operator && filters.value && (
            <Badge variant='secondary' className='gap-1'>
              {filters.field} {filters.operator} "{filters.value}"
              <X
                className='w-3 h-3 cursor-pointer'
                onClick={() => {
                  clearFilter('field')
                  clearFilter('operator')
                  clearFilter('value')
                }}
              />
            </Badge>
          )}

          {selectedInvoiceStatuses.length > 0 && (
            <Badge variant='secondary' className='gap-1'>
              Invoice Status:{' '}
              {selectedInvoiceStatuses.map(formatLabel).join(', ')}
              <X
                className='w-3 h-3 cursor-pointer'
                onClick={() => clearFilter('invoice_status')}
              />
            </Badge>
          )}

          {selectedPaymentStatuses.length > 0 && (
            <Badge variant='secondary' className='gap-1'>
              Payment Status:{' '}
              {selectedPaymentStatuses.map(formatLabel).join(', ')}
              <X
                className='w-3 h-3 cursor-pointer'
                onClick={() => clearFilter('payment_status')}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}

// InvoiceFiltersType is already exported from the interface definition above
